// src/config/threading.ts
// Centralized comment threading configuration for safe, controlled implementation

export const THREADING_CONFIG = {
  // Core threading settings
  maxDepth: 4, // Maximum nesting levels (0 = top-level, 1 = reply, 2 = reply to reply, etc.)
  maxRepliesShown: 3, // Number of replies shown before "Show more" button
  collapseThreshold: 10, // Auto-collapse threads with more than X replies

  // Visual settings
  indentationPx: 32, // Pixels to indent each threading level
  maxIndentationPx: 128, // Maximum total indentation (prevents ultra-narrow comments)
  showConnectorLines: true, // Show visual lines connecting threaded comments

  // Performance settings
  loadRepliesOnDemand: true, // Load replies when thread is expanded (vs all at once)
  replyBatchSize: 5, // Number of replies to load per request when on-demand loading

  // User experience settings
  defaultExpanded: true, // Show threads expanded by default
  autoScrollToReply: false, // Scroll to new reply after posting
  highlightNewReplies: true, // Visually highlight newly added replies

  // Safety limits
  maxContentLength: 2000, // Character limit for threaded replies (same as main comments)
  rateLimitReplies: true, // Apply rate limiting to rapid replies
  minTimeBetweenReplies: 10, // Seconds between replies from same user

  // Moderation settings
  autoCollapseReported: true, // Auto-collapse threads with reported content
  requireApprovalAtDepth: null, // Require approval for replies at certain depth (null = no limit)

  // Feature flags (for gradual rollout)
  enableThreading: true, // Master switch for threading feature
  enableThreadingForNewPosts: true, // Enable for new posts only initially
  enableThreadingForExistingPosts: false, // Roll out to existing posts later

  // Mobile optimizations
  mobileMaxDepth: 3, // Reduce max depth on mobile devices
  mobileIndentationPx: 24, // Smaller indentation on mobile
  mobileCollapseThreshold: 5, // More aggressive collapse on mobile

  // Accessibility settings
  announceThreadLevel: true, // Announce threading level to screen readers
  keyboardNavigationEnabled: true, // Enable keyboard navigation through threads
  highContrastConnectors: false, // Use high contrast connector lines

  // Analytics tracking (for understanding user behavior)
  trackThreadingInteractions: true, // Track expand/collapse, reply depths, etc.
  trackPerformanceMetrics: true, // Track load times, rendering performance
};

// Helper function to get threading config with environment overrides
export const getThreadingConfig = () => {
  // Allow environment variable overrides for key settings
  const config = { ...THREADING_CONFIG };

  // Override with environment variables if present
  if (import.meta.env.VITE_THREADING_MAX_DEPTH) {
    config.maxDepth = parseInt(import.meta.env.VITE_THREADING_MAX_DEPTH);
  }

  if (import.meta.env.VITE_THREADING_ENABLED === 'false') {
    config.enableThreading = false;
  }

  return config;
};

// Helper function to check if threading is enabled
export const isThreadingEnabled = () => {
  return getThreadingConfig().enableThreading;
};

// Helper function to get max depth for current device
export const getMaxDepthForDevice = (isMobile = false) => {
  const config = getThreadingConfig();
  return isMobile ? config.mobileMaxDepth : config.maxDepth;
};

// Helper function to get indentation for current device
export const getIndentationForDevice = (isMobile = false) => {
  const config = getThreadingConfig();
  return isMobile ? config.mobileIndentationPx : config.indentationPx;
};

// Helper function to calculate thread depth from comment structure
export const calculateThreadDepth = (comment: { parent_id?: string }, allComments: { id: string; parent_id?: string }[]) => {
  let depth = 0;
  let currentParentId = comment.parent_id;

  // Walk up the parent chain to calculate depth
  while (currentParentId && depth < THREADING_CONFIG.maxDepth) {
    const parent = allComments.find((c) => c.id === currentParentId);
    if (!parent) break;

    depth++;
    currentParentId = parent.parent_id;
  }

  return depth;
};

// Helper function to check if a reply should be collapsed
export const shouldCollapseThread = (_comment: unknown, replyCount: number, isMobile = false) => {
  const config = getThreadingConfig();
  const threshold = isMobile ? config.mobileCollapseThreshold : config.collapseThreshold;

  return replyCount > threshold;
};

// Helper function to validate threading depth before allowing reply
export const canReplyAtDepth = (currentDepth: number, isMobile = false) => {
  const maxDepth = getMaxDepthForDevice(isMobile);
  return currentDepth < maxDepth;
};

// Configuration metadata for admin dashboard
export const THREADING_META = {
  version: '1.0.0',
  description: 'Comment threading configuration for enhanced community discussions',
  lastUpdated: '2025-01-22',
  featureStage: 'implementation', // planning -> implementation -> rollout -> stable
};

// Validation function to ensure config values are safe
export const validateThreadingConfig = (config = THREADING_CONFIG) => {
  const warnings = [];

  if (config.maxDepth > 6) {
    warnings.push('Max depth above 6 may cause UX issues');
  }

  if (config.indentationPx < 16) {
    warnings.push('Indentation below 16px may be hard to see');
  }

  if (config.maxIndentationPx < config.indentationPx * config.maxDepth) {
    warnings.push('Max indentation may be exceeded at max depth');
  }

  if (config.replyBatchSize > 20) {
    warnings.push('Large batch sizes may impact performance');
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
};

export default THREADING_CONFIG;
